/**
 * PWA: registro do service worker e o pedido de instalação no celular.
 *
 * O Android (Chrome) avisa que o app pode ser instalado com o evento `beforeinstallprompt`, que
 * pode chegar antes de a tela existir: por isso é capturado aqui, assim que o site carrega.
 * O iPhone não tem esse evento: a instalação é pelo menu Compartilhar do Safari.
 */

interface EventoInstalacao extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let pedidoGuardado: EventoInstalacao | null = null;
let instalado = false;
const ouvintes = new Set<() => void>();
const avisar = () => ouvintes.forEach((o) => o());

const temJanela = typeof window !== 'undefined';

if (temJanela) {
  window.addEventListener('beforeinstallprompt', (e) => {
    // Sem o aviso padrão do navegador: o MetaEpi mostra o seu, com "Agora não"
    e.preventDefault();
    pedidoGuardado = e as EventoInstalacao;
    avisar();
  });
  window.addEventListener('appinstalled', () => {
    instalado = true;
    pedidoGuardado = null;
    avisar();
  });
}

/** Registra o service worker (só no build de produção: no desenvolvimento ele atrapalharia o Vite). */
export function registrarServiceWorker() {
  if (!temJanela || !('serviceWorker' in navigator) || !import.meta.env.PROD) return;
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {
      // Sem service worker o sistema funciona igual; só não fica instalável
    });
  });
}

export function ouvirInstalacao(ouvinte: () => void): () => void {
  ouvintes.add(ouvinte);
  return () => ouvintes.delete(ouvinte);
}

/** Já está aberto como app instalado (tela cheia)? */
export function rodandoComoApp(): boolean {
  if (!temJanela) return false;
  const standalone =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(display-mode: standalone)').matches;
  return instalado || standalone || (navigator as { standalone?: boolean }).standalone === true;
}

/** iPhone/iPad no Safari: instala pelo menu Compartilhar. */
export function ehIphone(
  ua = typeof navigator === 'undefined' ? '' : navigator.userAgent,
): boolean {
  // iPadOS se apresenta como Mac, mas tem tela de toque
  const ipad =
    /Macintosh/.test(ua) && typeof navigator !== 'undefined' && navigator.maxTouchPoints > 1;
  return /iPhone|iPad|iPod/.test(ua) || ipad;
}

/** Aparelho de toque (celular ou tablet): o aviso só aparece nele. */
export function ehCelular(): boolean {
  if (!temJanela) return false;
  if (ehIphone()) return true;
  return typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
}

export const podeInstalarDireto = () => pedidoGuardado !== null;

/** Abre a janela de instalação do próprio Android. Devolve se a pessoa aceitou. */
export async function instalar(): Promise<boolean> {
  const pedido = pedidoGuardado;
  if (!pedido) return false;
  await pedido.prompt();
  const { outcome } = await pedido.userChoice;
  pedidoGuardado = null;
  avisar();
  return outcome === 'accepted';
}

const CHAVE_DISPENSADO = 'metaepi.instalacao.dispensada';

export function avisoDispensado(): boolean {
  try {
    return localStorage.getItem(CHAVE_DISPENSADO) === '1';
  } catch {
    return false;
  }
}

export function dispensarAviso() {
  try {
    localStorage.setItem(CHAVE_DISPENSADO, '1');
  } catch {
    // Sem armazenamento (modo privado): o aviso some até recarregar
  }
}
